import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableHead,
    Toolbar, Typography, CircularProgress, TablePagination, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Autocomplete, Stack, IconButton, Alert
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import PageHelp from "../components/common/PageHelp";
import {
    getRideOptions,
    getRideOptionDetails,
    updateRideOption,
    createRideOption,
    deleteRideOption
} from "../services/rideOptionService";
import { getAllStates, getCitiesByState } from "../services/referenceService"; // Added for city lookups
import {
    StyledTableContainer,
    StyledTableCell,
    StyledTableRow,
    StyledPaper,
} from "../assets/styles/tableStyle";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";

type RideOption = {
    id: number;
    name: string;
    type: string;
    label: string;
    seats: number;
    price: string;
    info: string;
    available: boolean;
    show: boolean;
    order: number;
    category?: string;
    iconKey?: string;
    description?: string;
    baseFare: number;
    perKm: number;
    pickupCharges: number;
    minKm: number;
    waitingCharges: number;
    nightBaseFare?: number;
    nightPerKm?: number;
    driverAdditions?: number;
    congestionCharges?: number;
    maxDistance?: number;
    location?: string;
    excludedCityIds?: string[]; // Added this field
    createdAt?: string;
    updatedAt?: string;
};

const booleanFields = ["available", "show"] as const;
const allFields = [
    "name", "type", "label", "seats", "price", "info", "order", "category", "iconKey",
    "description", "baseFare", "perKm", "pickupCharges",
    "minKm", "waitingCharges", "nightBaseFare", "nightPerKm", "driverAdditions", "congestionCharges",
    "maxDistance", "location", ...booleanFields
] as const;

const numberFields = [
    "seats", "order", "baseFare", "perKm", "pickupCharges",
    "minKm", "waitingCharges", "nightBaseFare", "nightPerKm", "driverAdditions", "congestionCharges",
    "maxDistance"
];

const RideOptionList: React.FC = () => {
    const [items, setItems] = useState<RideOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    // Edit/Create modal state
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedItem, setSelectedItem] = useState<RideOption | null>(null);
    const [editData, setEditData] = useState<Partial<RideOption>>({});
    const [editLoading, setEditLoading] = useState<boolean>(false);
    const [modalMode, setModalMode] = useState<"edit" | "create">("edit");

    // Reference Data for Cities
    const [statesList, setStatesList] = useState<any[]>([]);
    const [citiesList, setCitiesList] = useState<any[]>([]);
    const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

    // Context menu
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<RideOption | null>(null);

    useEffect(() => {
        fetchItems();
        loadStates();
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await getRideOptions();
            setItems(res.data);
        } finally {
            setLoading(false);
        }
    };

    const loadStates = async () => {
        try {
            const res = await getAllStates();
            if (res.data.success) {
                setStatesList(res.data.states);
            }
        } catch (e) { console.error(e); }
    };

    const handleStateChange = async (stateCode: string | null) => {
        setSelectedStateCode(stateCode);
        setCitiesList([]);
        if (stateCode) {
            try {
                const res = await getCitiesByState(stateCode);
                if (res.data.success) {
                    setCitiesList(res.data.cities);
                }
            } catch (e) { console.error(e); }
        }
    };

    // ====== MODAL HANDLING ======
    const openCreateModal = () => {
        setModalMode("create");
        setEditData({ excludedCityIds: [] });
        setSelectedId(null);
        setSelectedStateCode(null);
        setCitiesList([]);
        setDetailOpen(true);
    };

    const handleRowClick = async (id: number) => {
        setModalMode("edit");
        setSelectedId(id);
        setDetailOpen(true);
        setEditLoading(true);
        setSelectedStateCode(null);
        setCitiesList([]);
        try {
            const res = await getRideOptionDetails(id);
            // Ensure excludedCityIds is an array
            const data = res.data;
            if (!data.excludedCityIds) data.excludedCityIds = [];

            setSelectedItem(data);
            setEditData(data);
        } finally {
            setEditLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setDetailOpen(false);
        setSelectedId(null);
        setSelectedItem(null);
        setEditData({});
    };

    const handleFieldChange = (field: keyof RideOption, value: any) => {
        setEditData((prev) => ({ ...prev, [field]: value }));
    };

    // Helper to add a city to the exclusion list
    const handleAddExcludedCity = (city: string) => {
        if (!city) return;
        const currentList = editData.excludedCityIds || [];
        if (!currentList.includes(city)) {
            setEditData(prev => ({
                ...prev,
                excludedCityIds: [...currentList, city]
            }));
        }
    };

    // Helper to remove a city from the exclusion list
    const handleRemoveExcludedCity = (cityToRemove: string) => {
        const currentList = editData.excludedCityIds || [];
        setEditData(prev => ({
            ...prev,
            excludedCityIds: currentList.filter(c => c !== cityToRemove)
        }));
    };

    const handleSaveEdit = async () => {
        setEditLoading(true);
        try {
            const updateFields: any = {};
            // Include standard fields
            allFields.forEach((field) => {
                if (editData[field] !== undefined) updateFields[field] = editData[field];
            });
            // Explicitly include excludedCityIds
            if (editData.excludedCityIds !== undefined) {
                updateFields.excludedCityIds = editData.excludedCityIds;
            }

            if (modalMode === "edit" && selectedId) {
                await updateRideOption(selectedId, updateFields);
            } else if (modalMode === "create") {
                await createRideOption(updateFields);
            }
            await fetchItems();
            setDetailOpen(false);
        } finally {
            setEditLoading(false);
        }
    };

    // ====== CONTEXT MENU ======
    const handleContextMenu = (event: React.MouseEvent, row: RideOption) => {
        event.preventDefault();
        setSelectedRow(row);
        setMenu({ top: event.clientY, left: event.clientX });
    };

    const handleCloseMenu = () => setMenu(null);

    const handleEditContext = () => {
        if (selectedRow) {
            handleRowClick(selectedRow.id);
        }
        handleCloseMenu();
    };

    const handleDeleteContext = async () => {
        if (selectedRow) {
            setLoading(true);
            try {
                await deleteRideOption(selectedRow.id);
                await fetchItems();
            } finally {
                setLoading(false);
            }
        }
        handleCloseMenu();
    };

    const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Context Menu Actions
    const actions: ContextMenuAction[] = [
        {
            dividerBefore: true,
            label: "Edit",
            icon: <EditIcon color="info" fontSize="small" />,
            onClick: handleEditContext,
            color: "info",
        },
        {
            label: "Delete",
            icon: <DeleteIcon color="error" fontSize="small" />,
            onClick: handleDeleteContext,
            color: "error",
            dividerBefore: true,
        }
    ];

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6">Ride Options Management</Typography>
                  <PageHelp k="rideOptions" />
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openCreateModal}
                    sx={{ ml: 2 }}
                >
                    Add New
                </Button>
            </Toolbar>
            <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
                Fares are priced from <RouterLink to="/rate-cards">Rate Cards</RouterLink>. These fare fields (base, per km, pickup, min km, waiting, night) still set the live price of every card marked "Today's prices (unchanged)", so editing them changes those prices at once. They also price a vehicle with no card, and NEW bookings while RATE_CARD_PRICING_ENABLED is 0. Once a card is edited on the Rate Cards page, these fields no longer affect it.
            </Alert>
            {loading ? (
                <CircularProgress sx={{ margin: 2 }} />
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 1200 }}>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Excluded Cities</StyledTableCell>
                                {allFields.map(col => <StyledTableCell key={col}>{col.charAt(0).toUpperCase() + col.slice(1)}</StyledTableCell>)}
                                <StyledTableCell>Created At</StyledTableCell>
                                <StyledTableCell>Updated At</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.map((item) => (
                                <StyledTableRow
                                    key={item.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => handleRowClick(item.id)}
                                    onContextMenu={e => handleContextMenu(e, item)}
                                >
                                    <StyledTableCell>{item.id}</StyledTableCell>
                                    {/* Show count of excluded cities */}
                                    <StyledTableCell>
                                        {item.excludedCityIds && item.excludedCityIds.length > 0 ? (
                                            <Chip
                                                label={`${item.excludedCityIds.length} Hidden`}
                                                color="warning"
                                                size="small"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Chip label="Global" color="success" size="small" variant="outlined" />
                                        )}
                                    </StyledTableCell>
                                    {allFields.map(field => (
                                        <StyledTableCell key={field}>
                                            {booleanFields.includes(field as any)
                                                ? (item[field]
                                                    ? <Chip label="Yes" color="success" size="small"/>
                                                    : <Chip label="No" color="error" size="small"/>)
                                                : typeof item[field] === "number"
                                                    ? item[field]
                                                    : item[field] ?? "-"}
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell>{item.createdAt ? item.createdAt.slice(0, 19) : "-"}</StyledTableCell>
                                    <StyledTableCell>{item.updatedAt ? item.updatedAt.slice(0, 19) : "-"}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={items.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </StyledTableContainer>
            )}

            {/* Edit/Create Modal */}
            <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="lg">
                <DialogTitle>{modalMode === "edit" ? "Edit Ride Option" : "Create Ride Option"}</DialogTitle>
                <DialogContent>
                    {editLoading ? <CircularProgress /> : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>

                            {/* --- Section 1: Excluded Cities Management --- */}
                            <StyledPaper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #eee' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
                                    🚫 Excluded Cities (Blacklist)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    This vehicle type will NOT appear in the cities listed below.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                                    {/* 1. Filter State */}
                                    <Autocomplete
                                        options={statesList}
                                        getOptionLabel={(option) => option.name}
                                        value={statesList.find(s => s.isoCode === selectedStateCode) || null}
                                        onChange={(event, newValue) => {
                                            handleStateChange(newValue ? newValue.isoCode : null);
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="1. Filter State" size="small" />
                                        )}
                                        sx={{ width: 250 }}
                                    />
                                    {/* 2. Select City */}
                                    <Autocomplete
                                        options={citiesList}
                                        getOptionLabel={(option) => option.name}
                                        onChange={(event, newValue) => {
                                            if (newValue) handleAddExcludedCity(newValue.name);
                                        }}
                                        disabled={!selectedStateCode}
                                        renderInput={(params) => (
                                            <TextField {...params} label="2. Select City to Block" size="small" />
                                        )}
                                        sx={{ width: 250 }}
                                    />
                                </Box>

                                {/* 3. List of Blocked Cities */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {editData.excludedCityIds && editData.excludedCityIds.length > 0 ? (
                                        editData.excludedCityIds.map((city) => (
                                            <Chip
                                                key={city}
                                                label={city}
                                                onDelete={() => handleRemoveExcludedCity(city)}
                                                color="error"
                                                variant="outlined"
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#999' }}>
                                            No cities excluded. This option is visible globally.
                                        </Typography>
                                    )}
                                </Box>
                            </StyledPaper>

                            {/* --- Section 2: General Details (Flex Layout) --- */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 1 }}>
                                📋 General Details
                            </Typography>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                {/* Read-only fields */}
                                {modalMode === "edit" && selectedItem && (
                                    <>
                                        <TextField label="ID" value={selectedItem.id} disabled sx={{ flex: '1 1 30%' }} size="small" />
                                        <TextField label="Created At" value={selectedItem.createdAt || ""} disabled sx={{ flex: '1 1 30%' }} size="small" />
                                        <TextField label="Updated At" value={selectedItem.updatedAt || ""} disabled sx={{ flex: '1 1 30%' }} size="small" />
                                    </>
                                )}

                                {/* Editable fields loop */}
                                {allFields.filter(f => !booleanFields.includes(f as any)).map(field => (
                                    <TextField
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={editData[field as keyof RideOption] ?? ""}
                                        sx={{ flex: '1 1 45%' }} // Approx 2 columns
                                        size="small"
                                        onChange={e => {
                                            const isNumber = numberFields.includes(field);
                                            handleFieldChange(
                                                field as keyof RideOption,
                                                isNumber ? Number(e.target.value) : e.target.value
                                            );
                                        }}
                                        disabled={modalMode === "edit" && field === "iconKey"}
                                    />
                                ))}

                                {/* Boolean fields */}
                                {booleanFields.map(field => (
                                    <TextField
                                        select
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={
                                            editData[field as keyof RideOption] === undefined
                                                ? "no"
                                                : editData[field as keyof RideOption]
                                                ? "yes"
                                                : "no"
                                        }
                                        sx={{ flex: '1 1 45%' }}
                                        size="small"
                                        onChange={e =>
                                            handleFieldChange(
                                                field as keyof RideOption,
                                                e.target.value === "yes"
                                            )
                                        }
                                    >
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </TextField>
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveEdit}
                        disabled={editLoading}
                    >
                        {modalMode === "edit" ? "Save" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <FancyContextMenu
                actions={actions}
                open={Boolean(menu)}
                onClose={handleCloseMenu}
                anchorPosition={menu}
                title={selectedRow?.label || selectedRow?.type || "Row Actions"}
            />
        </StyledPaper>
    );
};

export default RideOptionList;
